import { useQuestionnaireContext } from "@/contexts/QuestionnaireContext";
import { useAttendanceSpecialtyIndex } from "@/hooks/useAttendanceSpecialtyIndex";
import { buildQrDisplayInfo } from "@/utils/resolveAttendanceSpecialty";
import { ExamResult, GruposExames } from "./types";

/**
 * Histórico FHIR grava "Data de realização" em valueString.
 * Exames manuais e a UI (filtros/card/tabela) usam valueDate.
 * Copia o valor para valueDate sem mutar o resource original.
 */
const withNormalizedExamDate = (response: any) => {
  if (!response?.item) return response;

  return {
    ...response,
    item: response.item.map((examItem: any) => {
      if (examItem?.text !== "Data de realização") return examItem;

      const answer = examItem?.answer?.[0];
      // Já tem valueDate (exame novo) ou não tem data em valueString: não altera.
      if (!answer || answer.valueDate || !answer.valueString) return examItem;

      return {
        ...examItem,
        answer: [{ ...answer, valueDate: answer.valueString }],
      };
    }),
  };
};

export const useExamDataProcessing = () => {
  const { historyValues } = useQuestionnaireContext();
  const specialtyIndex = useAttendanceSpecialtyIndex();

  const processHistoricalData = () => {
    const responseEntries = historyValues?.map((el: any) => el?.resource);

    const examResultResponses = responseEntries?.map((itemResponse: any) => {
      const questionnaireResponseId = itemResponse?.id;
      const patient = itemResponse?.subject?.reference?.split("-")?.[1];
      const answerDate = itemResponse?.authored;
      const filterItems = itemResponse?.item
        ?.filter((e: any) => e?.text === "Resultado de exames")?.[0]
        ?.item?.filter((subItem: any) => {
          return subItem?.item?.[0]?.answer;
        });
      const displayInfo = buildQrDisplayInfo(itemResponse, specialtyIndex);
      return { answerDate, filterItems, displayInfo, questionnaireResponseId, patient };
    });

    return examResultResponses
      ?.filter((el: any) => el?.filterItems?.length)
      ?.flatMap((item: any) => {
        const auxItem = item?.filterItems?.map((response: any) => {
          const normalizedResponse = withNormalizedExamDate(response);
          return {
            ...normalizedResponse,
            displayInfo: item?.displayInfo,
            answerDate: item?.answerDate,
            questionnaireResponseId: item?.questionnaireResponseId,
            patient: item?.patient,
          };
        });
        return auxItem;
      });
  };

  const groupExamsByName = (exams: ExamResult[]): GruposExames => {
    const grupos: GruposExames = {};
    exams?.forEach((exame) => {
      const nomeExameItem = exame?.item?.find(
        (item) => item?.text === "Nome do exame"
      );
      if (nomeExameItem) {
        const nomeExame = nomeExameItem?.answer?.[0]?.valueString;
        if (nomeExame === undefined) return;
        if (!grupos[nomeExame]) {
          grupos[nomeExame] = [];
        }
        grupos[nomeExame].push(exame);
      }
    });
    return grupos;
  };

  return {
    processHistoricalData,
    groupExamsByName,
  };
};
